import React, { useEffect, useState } from 'react';

function LeadScoredJobs() {
  const [leadSearch, setLeadSearch] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [leadSearchLoading, setLeadSearchLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadSelected, setLeadSelected] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // Made optional
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  // Sorting state for Score column: null | 'asc' | 'desc'
  const [scoreSort, setScoreSort] = useState('desc');
  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [total, setTotal] = useState(0);
  // Chart data for last 28 days
  const [dailyCounts, setDailyCounts] = useState([]);

  // Date helpers (avoid UTC off-by-one)
  const formatYMDLocal = (date) => {
    if (!(date instanceof Date)) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const parseYMDLocal = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split('-').map((v) => parseInt(v, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };


  // Calendar component
  const Calendar = ({ selectedDate, onDateSelect, onClose }) => {
    const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
    
    const getDaysInMonth = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDayOfWeek = firstDay.getDay();
      
      const days = [];
      
      // Add empty cells for days before the first day of the month
      for (let i = 0; i < startingDayOfWeek; i++) {
        days.push(null);
      }
      
      // Add days of the month
      for (let day = 1; day <= daysInMonth; day++) {
        days.push(new Date(year, month, day));
      }
      
      return days;
    };

    const isToday = (date) => {
      const today = new Date();
      return date && 
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
    };

    const isSameDay = (date1, date2) => {
      return date1 && date2 &&
        date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear();
    };

    const days = getDaysInMonth(currentMonth);
    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];

    return (
      <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
            className="p-1 hover:bg-gray-100 rounded"
          >
            &#8249;
          </button>
          <h3 className="text-lg font-semibold">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
            className="p-1 hover:bg-gray-100 rounded"
          >
            &#8250;
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => (
            <button
              key={index}
              onClick={() => {
                if (day) {
                  onDateSelect(day);
                  onClose();
                }
              }}
              disabled={!day}
              className={`
                h-8 w-8 text-sm rounded hover:bg-blue-100 
                ${!day ? 'invisible' : ''}
                ${isToday(day) ? 'bg-blue-200 font-bold' : ''}
                ${isSameDay(day, selectedDate) ? 'bg-blue-500 text-white' : ''}
                ${day && !isToday(day) && !isSameDay(day, selectedDate) ? 'hover:bg-gray-100' : ''}
              `}
            >
              {day ? day.getDate() : ''}
            </button>
          ))}
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between">
          <button
            onClick={() => {
              onDateSelect(new Date());
              onClose();
            }}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Today
          </button>
          <button
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Debounced server search for leads when user types
  useEffect(() => {
    if (!leadSearch || leadSearch.length < 2 || leadSelected) {
      setLeadResults([]);
      setShowLeadDropdown(false);
      setLeadSearchLoading(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setLeadSearchLoading(true);
        setShowLeadDropdown(true);
        const response = await fetch(`/api/v1/leads/?page=1&search=${encodeURIComponent(leadSearch)}`);
        const data = await response.json();
        setLeadResults(data.results || []);
      } catch (err) {
        setLeadResults([]);
        setShowLeadDropdown(true);
      } finally {
        setLeadSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [leadSearch, leadSelected]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close calendar if clicking outside
      if (showCalendar && !event.target.closest('.calendar-container')) {
        setShowCalendar(false);
      }
      // Close lead dropdown if clicking outside
      if (showLeadDropdown && !event.target.closest('.lead-search-container')) {
        setShowLeadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar, showLeadDropdown]);

  // Fetch jobs when lead or date changes
  useEffect(() => {
    if (!selectedLead) {
      setJobs([]);
      return;
    }
    setLoading(true);
    
    // Build API URL - date is optional filter
  let apiUrl = `/api/lead-scored-jobs?leadId=${selectedLead.id}&page=${page}&pageSize=${pageSize}`;
    if (selectedDate) {
      const apiDate = formatYMDLocal(selectedDate);
      apiUrl += `&date=${apiDate}`;
    }
    
    fetch(apiUrl)
      .then(res => res.json())
      .then(data => {
        setJobs(data.results || []);
        setTotal(data.total || 0);
        setLoading(false);
      })
      .catch(err => {
        setJobs([]);
        setLoading(false);
      });
  }, [selectedLead, selectedDate, page]);

  // Reset to first page when lead or date changes
  useEffect(() => {
    setPage(1);
  }, [selectedLead, selectedDate]);

  // Reset to first page when score sort changes
  useEffect(() => {
    setPage(1);
  }, [scoreSort]);

  // UI state for expanding/collapsing long reasoning text per row
  const [expandedReasoning, setExpandedReasoning] = useState({});
  const toggleReasoning = (id) =>
    setExpandedReasoning((prev) => ({ ...prev, [id]: !prev[id] }));

  // Date navigation
  const changeDate = (days) => {
    if (selectedDate) {
      setSelectedDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() + days);
        return newDate;
      });
    } else {
      // If no date selected, start with today
      const today = new Date();
      today.setDate(today.getDate() + days);
      setSelectedDate(today);
    }
  };

  // Clear date filter
  const clearDateFilter = () => {
    setSelectedDate(null);
  };

  // Fetch last 28 days daily counts whenever lead changes (or on mount)
  useEffect(() => {
    if (!selectedLead) {
      setDailyCounts([]);
      return;
    }
    const url = `/api/lead-scored-jobs/daily-counts/?leadId=${selectedLead.id}`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => setDailyCounts(data.results || []))
      .catch(() => setDailyCounts([]));
  }, [selectedLead]);

  const maxDaily = dailyCounts.length ? Math.max(...dailyCounts.map((d) => d.count)) : 0;
  const colorForIndex = (i) => {
    // Pleasant categorical palette cycling
    const palette = ['#60a5fa', '#34d399', '#f59e0b', '#f472b6', '#a78bfa', '#f87171', '#22d3ee'];
    return palette[i % palette.length];
  };

  // Render scored jobs table
  const renderJobsTable = () => {
    if (!selectedLead) {
      return (
        <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center justify-center min-h-[300px]">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.657 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 616 0z" /></svg>
          <div className="text-xl font-semibold text-gray-700 mb-2">No lead selected</div>
          <div className="text-gray-500 text-lg">Please select a lead to view their scored jobs.</div>
        </div>
      );
    }
    
    if (loading) {
      return (
        <div className="bg-white rounded-xl shadow p-8 flex items-center justify-center min-h-[300px]">
          <div className="text-lg text-gray-600">Loading scored jobs...</div>
        </div>
      );
    }
    
    if (!jobs.length) {
      const dateText = selectedDate ? ` on ${selectedDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}` : '';
      return (
        <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center justify-center min-h-[300px]">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.657 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 616 0z" /></svg>
          <div className="text-xl font-semibold text-gray-700 mb-2">No scored jobs found</div>
          <div className="text-gray-500 text-lg">No scored jobs found for this lead{dateText}.</div>
        </div>
      );
    }

    // Prepare sorted jobs for display (sort by score percent)
    const toPercent = (s) => {
      if (typeof s !== 'number' || Number.isNaN(s)) return -1;
      return s <= 1 ? Math.round(s * 100) : Math.round(s);
    };
    const displayedJobs = scoreSort
      ? [...jobs].sort((a, b) => {
          const ap = toPercent(a.score);
          const bp = toPercent(b.score);
          return scoreSort === 'asc' ? ap - bp : bp - ap;
        })
      : jobs;

    return (
  <div className="bg-white rounded-xl shadow">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            Scored Jobs for {selectedLead.name}
            {selectedDate && (
              <span className="text-sm font-normal text-gray-600 ml-2">
                on {selectedDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
              </span>
            )}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{total} jobs found</p>
        </div>

        {/* Mini bar chart for last 28 days */}
        {dailyCounts && dailyCounts.length > 0 && (
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="mb-2 text-sm font-medium text-gray-700">Jobs scored in last 28 days</div>
            <div className="relative h-24 flex items-end gap-1">
              {dailyCounts.map((d, i) => {
                const h = maxDaily ? Math.max(4, Math.round((d.count / maxDaily) * 88)) : 4; // px height
                const color = colorForIndex(i);
                const dateObj = parseYMDLocal(d.date);
                const label = dateObj ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : d.date;
                return (
                  <button
                    key={d.date}
                    type="button"
                    onClick={() => setSelectedDate(parseYMDLocal(d.date))}
                    className="group relative flex-1 min-w-[6px]"
                    aria-label={`${label}: ${d.count} jobs`}
                  >
                    <div
                      className="mx-auto w-full rounded-t"
                      style={{ height: `${h}px`, backgroundColor: color, opacity: d.count ? 1 : 0.25 }}
                    />
                    <div className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="rounded bg-white px-2 py-1 text-[10px] leading-3 text-gray-800 shadow border border-gray-200 whitespace-nowrap">
                        {label} • {d.count}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-1 flex justify-between text-[10px] text-gray-500">
              <span>{dailyCounts[0] && (parseYMDLocal(dailyCounts[0].date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '')}</span>
              <span>{dailyCounts[dailyCounts.length-1] && (parseYMDLocal(dailyCounts[dailyCounts.length-1].date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '')}</span>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    onClick={() => setScoreSort((prev) => (prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc'))}
                    className="inline-flex items-center gap-1 hover:text-gray-700"
                    title="Sort by score"
                  >
                    Score
                    {scoreSort === 'asc' && <span aria-hidden>▲</span>}
                    {scoreSort === 'desc' && <span aria-hidden>▼</span>}
                  </button>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayedJobs.map((job, index) => {
                const scorePercentage = toPercent(job.score);
                const scoreColor = scorePercentage >= 90 ? 'text-green-600 bg-green-100' : 
                                   scorePercentage >= 1 ? 'text-yellow-600 bg-yellow-100' : 
                                   'text-red-600 bg-red-100';
                
                const jobDate = job.generatedAt ? new Date(job.generatedAt.replace('Z', '')).toLocaleDateString('en-US', { 
                  month: 'numeric', 
                  day: 'numeric', 
                  year: 'numeric' 
                }) : '';

                const onRowClick = () => {
                  if (job.jobUrl) {
                    window.open(job.jobUrl, '_blank', 'noopener');
                  }
                };
                const isExpanded = !!expandedReasoning[job.id || index];
                const reasoningText = (job.reasoning || '').trim();
                const previewLimit = 240;
                const visibleReasoning =
                  isExpanded || reasoningText.length <= previewLimit
                    ? reasoningText
                    : reasoningText.slice(0, previewLimit) + '...';
                return (
                  <tr key={job.id || index} className="hover:bg-gray-50 cursor-pointer" onClick={onRowClick}>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-semibold text-gray-900">
                          {job.jobTitle || 'Untitled Job'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {job.companyName || 'Unknown Company'}
                          {(job.companyName && (job.location || 'Location varies')) ? ' • ' : ' '}
                          {job.location || 'Location varies'}
                        </div>

                        {/* score breakdown chips removed per request */}

                        {reasoningText && (
                          <div className="mt-2 text-sm leading-5 text-gray-700">
                            <span className="whitespace-pre-wrap">{visibleReasoning}</span>
                            {reasoningText.length > previewLimit && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleReasoning(job.id || index); }}
                                className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
                              >
                                {isExpanded ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </div>
                        )}

                        {job.jobUrl && (
                          <div className="mt-1">
                            <a
                              href={job.jobUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Open job posting →
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${scoreColor}`}>
                        {scorePercentage}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">
                      {jobDate}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 rounded bg-gray-100 text-gray-600 disabled:opacity-50"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <button
              className="px-3 py-1 rounded bg-gray-100 text-gray-600 disabled:opacity-50"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(total / pageSize)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };
  

  return (
    <div className="w-full max-w-6xl mx-auto mt-8">
      <h2 className="text-2xl font-semibold text-gray-700 mb-4">View scored jobs for leads with detailed reasoning and filters</h2>
      <div className="bg-white rounded-xl shadow p-8 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <div>
            <label className="block text-lg font-bold text-gray-700 mb-2">Select Lead *</label>
            <div className="relative lead-search-container">
              <input
                type="text"
                placeholder="Type to search leads (2+ chars)..."
                value={leadSearch}
                onChange={(e) => { 
                  const v = e.target.value; 
                  setLeadSearch(v); 
                  setLeadSelected(false); // Allow dropdown to show again when typing
                  if (!v) {
                    setSelectedLead(null);
                  }
                }}
                className="w-full border rounded-lg px-4 py-3 text-xl text-gray-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {showLeadDropdown && (
                <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto">
                  {leadSearchLoading ? (
                    <div className="p-3 text-sm text-gray-500 flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                      <span>Searching…</span>
                    </div>
                  ) : leadResults.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500">No leads found</div>
                  ) : leadResults.map(lead => (
                    <button 
                      key={lead.id} 
                      type="button" 
                      onClick={() => {
                        setSelectedLead(lead);
                        setLeadSearch(lead.name);
                        setLeadSelected(true);
                        setShowLeadDropdown(false);
                      }} 
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium">{lead.name}</div>
                      <div className="text-xs text-gray-500">{lead.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedLead && (
              <div className="mt-2 text-sm text-gray-700">
                Selected: <span className="font-medium">{selectedLead.name}</span>
              </div>
            )}
          </div>
          <div>
            <label className="block text-lg font-bold text-gray-700 mb-2">
              Filter by Date (Optional)
              {selectedDate && (
                <button 
                  onClick={clearDateFilter}
                  className="ml-2 text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Clear
                </button>
              )}
            </label>
            <div className="flex gap-2 items-center relative calendar-container">
              <button 
                onClick={() => changeDate(-1)} 
                className="px-3 py-2 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                disabled={!selectedDate}
              >
                &#60;
              </button>
              <button
                onClick={() => setShowCalendar(!showCalendar)}
                className="px-4 py-2 border rounded bg-white font-semibold text-lg min-w-[140px] text-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {selectedDate ? selectedDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : 'All dates'}
              </button>
              <button 
                onClick={() => changeDate(1)} 
                className="px-3 py-2 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                disabled={!selectedDate}
              >
                &#62;
              </button>
              {!selectedDate && (
                <button 
                  onClick={() => setSelectedDate(new Date())} 
                  className="ml-2 px-3 py-2 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm"
                >
                  Select Today
                </button>
              )}
              
              {showCalendar && (
                <Calendar
                  selectedDate={selectedDate}
                  onDateSelect={(date) => setSelectedDate(date)}
                  onClose={() => setShowCalendar(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {renderJobsTable()}
    </div>
  );
}

export default LeadScoredJobs;
